/* eslint-disable */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

const NGOFirstRun: React.FC = () => {
  const { userProfile } = useAuth();
  const ngoId = userProfile?.ngo_id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState('');
  const [loadingData, setLoadingData] = useState(true);
  const [ngoStatus, setNgoStatus] = useState<string>('');
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [existingDocuments, setExistingDocuments] = useState<any>({});
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    legal_name: '',
    registration_type: '',
    pan: '',
    address: {
      street: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    contact: {
      name: '',
      email: '',
      phone: ''
    },
    bank_details: {
      account_number: '',
      ifsc: '',
      bank_name: '',
      branch: ''
    },
    documents: {
      csr1: null as File | null,
      '12a': null as File | null,
      '80g': null as File | null,
      fcra: null as File | null
    },
    logo: null as File | null,
    logo_url: null as string | null
  });

  useEffect(() => {
    if (ngoId) {
      loadNGOData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ngoId]);

  const loadNGOData = async () => {
    if (!ngoId) return;

    setLoadingData(true);
    try {
      const { data, error } = await supabase
        .from('ngos')
        .select('*')
        .eq('id', ngoId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error;
      }

      if (data) {
        setFormData({
          legal_name: data.legal_name || '',
          registration_type: data.registration_type || '',
          pan: data.pan || '',
          address: {
            street: data.address?.street || '',
            city: data.address?.city || '',
            state: data.address?.state || '',
            pincode: data.address?.pincode || '',
            country: data.address?.country || 'India'
          },
          contact: {
            name: data.contact?.name || '',
            email: data.contact?.email || '',
            phone: data.contact?.phone || ''
          },
          bank_details: {
            account_number: data.bank_details?.account_number || '',
            ifsc: data.bank_details?.ifsc || '',
            bank_name: data.bank_details?.bank_name || '',
            branch: data.bank_details?.branch || ''
          },
          documents: { csr1: null, '12a': null, '80g': null, fcra: null },
          logo: null,
          logo_url: data.logo_url || null
        });
        setNgoStatus(data.status || 'invited');
        setRejectionReason(data.rejection_reason || '');

        if (data.logo_url) {
          let path = data.logo_url;
          if (path.includes('/object/public/ngo-documents/')) path = path.split('/object/public/ngo-documents/')[1].split('?')[0];
          else if (path.includes('/ngo-documents/')) path = path.split('/ngo-documents/')[1].split('?')[0];
          if (path) {
            const { data: signedData } = await supabase.storage.from('ngo-documents').createSignedUrl(decodeURIComponent(path), 3600);
            if (signedData?.signedUrl) setLogoPreviewUrl(signedData.signedUrl);
          }
        }

        // Load existing documents
        const { data: docs, error: docsError } = await supabase
          .from('ngo_documents')
          .select('*')
          .eq('ngo_id', ngoId);

        if (!docsError && docs) {
          const docsMap: any = {};
          docs.forEach((doc: any) => {
            // Map based on document_type
            if (doc.document_type) {
              docsMap[doc.document_type] = doc.file_url;
            }
          });
          setExistingDocuments(docsMap);
        }
      }
    } catch (err: any) {
      console.error('Error loading NGO data:', err);
      // Don't show error if NGO doesn't exist yet (first time)
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name.startsWith('address.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: { ...prev.address, [field]: value }
      }));
    } else if (name.startsWith('contact.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        contact: { ...prev.contact, [field]: value }
      }));
    } else if (name.startsWith('bank_details.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        bank_details: { ...prev.bank_details, [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (docType: string, file: File | null) => {
    setFormData(prev => ({
      ...prev,
      documents: { ...prev.documents, [docType]: file }
    }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (file) {
      setFormData(prev => ({ ...prev, logo: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadDocument = async (file: File, docType: string): Promise<string> => {
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${ngoId}/${docType}-${Date.now()}-${safeName}`;
    const { error } = await supabase.storage.from('ngo-documents').upload(fileName, file);
    if (error) throw error;
    const { data: { publicUrl } } = supabase.storage.from('ngo-documents').getPublicUrl(fileName);
    return publicUrl;
  };

  const saveDraft = async () => {
    if (!ngoId) return;
    setSavingDraft(true);
    setError('');

    try {
      // Upload logo if provided
      let logoUrl = formData.logo_url;
      if (formData.logo) {
        logoUrl = await uploadDocument(formData.logo, 'logo');
        setFormData(prev => ({ ...prev, logo: null, logo_url: logoUrl }));
      }

      const { error: updateError } = await supabase
        .from('ngos')
        .update({
          legal_name: formData.legal_name,
          registration_type: formData.registration_type,
          pan: formData.pan,
          address: formData.address,
          contact: formData.contact,
          bank_details: formData.bank_details,
          logo_url: logoUrl,
          profile_complete: false
        })
        .eq('id', ngoId);

      if (updateError) throw updateError;

      alert('Draft saved successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!ngoId) throw new Error('NGO ID not found');

      // Upload logo if provided
      let logoUrl = formData.logo_url;
      if (formData.logo) {
        logoUrl = await uploadDocument(formData.logo, 'logo');
      }

      // Upload documents
      const documentUrls: any = {};
      for (const [docType, file] of Object.entries(formData.documents)) {
        if (file) {
          const url = await uploadDocument(file, docType);
          documentUrls[docType] = url;

          // Save document record
          await supabase.from('ngo_documents').insert({
            ngo_id: ngoId,
            document_type: docType === '12a' ? '12a' : docType === '80g' ? '80g' : docType === 'fcra' ? 'fcra' : 'csr1',
            file_url: url,
            file_name: file.name
          });
        }
      }

      // Update NGO profile - set status to 'submitted' for company verification
      // profile_complete remains false until company verifies (status = 'verified')
      const { error: updateError } = await supabase
        .from('ngos')
        .update({
          legal_name: formData.legal_name,
          registration_type: formData.registration_type,
          pan: formData.pan,
          address: formData.address,
          contact: formData.contact,
          bank_details: formData.bank_details,
          logo_url: logoUrl,
          status: 'submitted',
          profile_complete: false
        })
        .eq('id', ngoId);

      if (updateError) throw updateError;

      // Show success message - don't redirect, show pending verification message
      alert('Profile submitted successfully! Your profile is now pending company verification. You will be able to access the dashboard once verified.');

      // Close modal and refresh page to update status
      window.location.reload();
    } catch (err: any) {
      setError(err.message || 'Failed to submit profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div className="modal-content" style={{
        background: 'white',
        borderRadius: '12px',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        position: 'relative'
      }}>
        <div className="form-container" style={{ padding: '2rem' }}>
          <div className="form-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h1 style={{ margin: 0 }}>NGO Registration</h1>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={async () => {
                    await supabase.auth.signOut();
                    navigate('/login');
                  }}
                  className="btn btn-sm btn-outline"
                  style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                >
                  Sign Out
                </button>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0.5rem'
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>
            <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>
              {ngoStatus === 'submitted'
                ? 'Your profile is pending company verification. You can view your submitted information below.'
                : rejectionReason && rejectionReason.includes('Changes requested')
                  ? 'The company has requested changes to your profile. Please review and update the information below.'
                  : 'Complete your NGO profile to start collaborating on projects. Your profile will be sent to the company for verification.'}
            </p>

            {/* Professional Disclaimer */}
            {!ngoStatus || ngoStatus !== 'verified' ? (
              <div className="card" style={{
                background: '#eff6ff',
                border: '1px solid #3b82f6',
                marginBottom: '1.5rem',
                padding: '1.25rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <div style={{ fontSize: '1.5rem', lineHeight: 1 }}>ℹ️</div>
                  <div>
                    <h3 style={{ color: '#1e40af', marginBottom: '0.5rem', fontSize: '1rem', fontWeight: 600 }}>
                      Verification Required
                    </h3>
                    <p style={{ color: '#1e3a8a', margin: 0, fontSize: '0.9rem', lineHeight: 1.6 }}>
                      Your profile must be verified by the company before you can access the dashboard and collaborate on projects.
                      Please complete and submit your profile information. Once submitted, the company will review your details
                      and notify you upon verification. You will not be able to proceed further until your profile has been verified.
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Rejection Banner - only show for 'Changes requested' */}
            {rejectionReason && rejectionReason.includes('Changes requested') && (
              <div className="card" style={{ background: '#fef3c7', border: '1px solid #f59e0b', marginBottom: '1rem' }}>
                <h3 style={{ color: '#92400e', marginBottom: '0.5rem' }}>⚠️ Changes Requested</h3>
                <p style={{ color: '#78350f', margin: 0 }}>{rejectionReason.replace('Changes requested: ', '')}</p>
              </div>
            )}

            {/* Rejection Banner - show for actual rejection */}
            {ngoStatus === 'rejected' && rejectionReason && !rejectionReason.includes('Changes requested') && (
              <div className="card" style={{ background: '#fee2e2', border: '1px solid #ef4444', marginBottom: '1rem' }}>
                <h3 style={{ color: '#991b1b', marginBottom: '0.5rem' }}>❌ Application Rejected</h3>
                <p style={{ color: '#7f1d1d', margin: 0 }}>
                  <strong>Reason: </strong>{rejectionReason}
                </p>
                <p style={{ color: '#7f1d1d', marginTop: '0.5rem', fontSize: '0.875rem' }}>
                  Please update your information and resubmit for review.
                </p>
              </div>
            )}

            {/* Also show rejection banner if status is rejected but no reason given */}
            {ngoStatus === 'rejected' && !rejectionReason && (
              <div className="card" style={{ background: '#fee2e2', border: '1px solid #ef4444', marginBottom: '1rem' }}>
                <h3 style={{ color: '#991b1b', marginBottom: '0.5rem' }}>❌ Application Rejected</h3>
                <p style={{ color: '#7f1d1d', margin: 0 }}>
                  Your application has been rejected. Please update your information and resubmit.
                </p>
              </div>
            )}

            {error && (
              <div className="toast toast-error" style={{ position: 'relative', marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <h2>Basic Information</h2>



              <div style={{ display: 'flex', gap: '2rem', alignItems: 'center', marginBottom: '2rem' }}>
                <div
                  onClick={() => logoFileInputRef.current?.click()}
                  style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    backgroundColor: '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    border: '2px dashed #d1d5db',
                    position: 'relative',
                    flexShrink: 0
                  }}
                >
                  {logoPreviewUrl ? (
                    <img src={logoPreviewUrl} alt="Logo Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ textAlign: 'center', color: '#9ca3af' }}>
                      <div style={{ fontSize: '24px' }}>📷</div>
                      <div style={{ fontSize: '12px' }}>Logo</div>
                    </div>
                  )}
                  <input
                    type="file"
                    ref={logoFileInputRef}
                    onChange={handleLogoChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Legal Name *</label>
                    <input
                      type="text"
                      name="legal_name"
                      value={formData.legal_name}
                      onChange={handleChange}
                      required
                      placeholder="Registered legal name"
                    />
                  </div>
                </div>
              </div>


              <div className="form-row">
                <div className="form-group">
                  <label>Registration Type *</label>
                  <select
                    name="registration_type"
                    value={formData.registration_type}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select type</option>
                    <option value="society">Society</option>
                    <option value="trust">Trust</option>
                    <option value="section_8">Section 8 Company</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>PAN</label>
                  <input
                    type="text"
                    name="pan"
                    value={formData.pan}
                    onChange={handleChange}
                    placeholder="ABCDE1234F"
                    maxLength={10}
                  />
                </div>
              </div>

              <h2 style={{ marginTop: '2rem' }}>Address</h2>

              <div className="form-group">
                <label>Street Address *</label>
                <input
                  type="text"
                  name="address.street"
                  value={formData.address.street}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    name="address.city"
                    value={formData.address.city}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>State *</label>
                  <input
                    type="text"
                    name="address.state"
                    value={formData.address.state}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Pincode *</label>
                  <input
                    type="text"
                    name="address.pincode"
                    value={formData.address.pincode}
                    onChange={handleChange}
                    required
                    pattern="[0-9]{6}"
                    maxLength={6}
                  />
                </div>

                <div className="form-group">
                  <label>Country *</label>
                  <input
                    type="text"
                    name="address.country"
                    value={formData.address.country}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <h2 style={{ marginTop: '2rem' }}>Contact Information</h2>

              <div className="form-group">
                <label>Contact Name *</label>
                <input
                  type="text"
                  name="contact.name"
                  value={formData.contact.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="contact.email"
                    value={formData.contact.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Phone *</label>
                  <input
                    type="tel"
                    name="contact.phone"
                    value={formData.contact.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <h2 style={{ marginTop: '2rem' }}>Bank Details</h2>

              <div className="form-group">
                <label>Account Number</label>
                <input
                  type="text"
                  name="bank_details.account_number"
                  value={formData.bank_details.account_number}
                  onChange={handleChange}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>IFSC Code</label>
                  <input
                    type="text"
                    name="bank_details.ifsc"
                    value={formData.bank_details.ifsc}
                    onChange={handleChange}
                    placeholder="ABCD0123456"
                  />
                </div>

                <div className="form-group">
                  <label>Bank Name</label>
                  <input
                    type="text"
                    name="bank_details.bank_name"
                    value={formData.bank_details.bank_name}
                    onChange={handleChange}
                  />
                </div>
              </div>



              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={saveDraft}
                  disabled={savingDraft}
                >
                  {savingDraft ? 'Saving...' : 'Save Draft'}
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Submitting...' : 'Submit for Verification'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NGOFirstRun;


